import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { extractError } from '../../api/client';
import notificationApi from '../../api/notifications';

const fetchItems = createAsyncThunk('notifications/fetchItems', async (params = {}, { rejectWithValue }) => {
  try {
    return await notificationApi.adminList(params);
  } catch (error) {
    return rejectWithValue(extractError(error));
  }
});

const createItem = createAsyncThunk('notifications/createItem', async (payload, { rejectWithValue }) => {
  try {
    return await notificationApi.adminCreate(payload);
  } catch (error) {
    return rejectWithValue(extractError(error));
  }
});

const updateItem = createAsyncThunk('notifications/updateItem', async ({ id, payload }, { rejectWithValue }) => {
  try {
    return await notificationApi.adminUpdate(id, payload);
  } catch (error) {
    return rejectWithValue(extractError(error));
  }
});

const deleteItem = createAsyncThunk('notifications/deleteItem', async (id, { rejectWithValue }) => {
  try {
    await notificationApi.adminRemove(id);
    return id;
  } catch (error) {
    return rejectWithValue(extractError(error));
  }
});

const sendNow = createAsyncThunk('notifications/sendNow', async (id, { rejectWithValue }) => {
  try {
    return await notificationApi.sendNow(id);
  } catch (error) {
    return rejectWithValue(extractError(error));
  }
});

const cancelItem = createAsyncThunk('notifications/cancelItem', async (id, { rejectWithValue }) => {
  try {
    return await notificationApi.cancel(id);
  } catch (error) {
    return rejectWithValue(extractError(error));
  }
});

const fetchConfig = createAsyncThunk('notifications/fetchConfig', async (_, { rejectWithValue }) => {
  try {
    return await notificationApi.getAdminConfig();
  } catch (error) {
    return rejectWithValue(extractError(error));
  }
});

const updateConfig = createAsyncThunk('notifications/updateConfig', async (payload, { rejectWithValue }) => {
  try {
    return await notificationApi.updateAdminConfig(payload);
  } catch (error) {
    return rejectWithValue(extractError(error));
  }
});

const sendTest = createAsyncThunk('notifications/sendTest', async (_, { rejectWithValue }) => {
  try {
    return await notificationApi.sendTest();
  } catch (error) {
    return rejectWithValue(extractError(error));
  }
});

const replaceItem = (state, payload) => {
  state.items = state.items.map((it) => (it.id === payload.id ? { ...it, ...payload } : it));
};

const notificationSlice = createSlice({
  name: 'notifications',
  initialState: {
    items: [],
    count: 0,
    status: 'idle',
    error: null,
    submitting: false,
    config: { enabled: true, subscription_count: 0 },
  },
  reducers: {
    clearNotificationError(state) {
      state.error = null;
    },
    setConfigEnabled(state, action) {
      state.config = { ...state.config, enabled: Boolean(action.payload) };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchItems.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchItems.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = action.payload.results ?? action.payload;
        state.count = action.payload.count ?? state.items.length;
      })
      .addCase(fetchItems.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload?.message || 'Failed to load notifications';
      })
      .addCase(createItem.pending, (state) => {
        state.submitting = true;
      })
      .addCase(createItem.fulfilled, (state, action) => {
        state.submitting = false;
        state.items = [action.payload, ...state.items];
        state.count += 1;
      })
      .addCase(createItem.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload?.message || 'Failed to create notification';
      })
      .addCase(updateItem.pending, (state) => {
        state.submitting = true;
      })
      .addCase(updateItem.fulfilled, (state, action) => {
        state.submitting = false;
        replaceItem(state, action.payload);
      })
      .addCase(updateItem.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload?.message || 'Failed to update notification';
      })
      .addCase(deleteItem.pending, (state) => {
        state.submitting = true;
      })
      .addCase(deleteItem.fulfilled, (state, action) => {
        state.submitting = false;
        state.items = state.items.filter((it) => it.id !== action.payload);
        state.count = Math.max(0, state.count - 1);
      })
      .addCase(deleteItem.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload?.message || 'Failed to delete notification';
      })
      .addCase(sendNow.pending, (state) => {
        state.submitting = true;
      })
      .addCase(sendNow.fulfilled, (state, action) => {
        state.submitting = false;
        replaceItem(state, action.payload);
      })
      .addCase(sendNow.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload?.message || 'Failed to send notification';
      })
      .addCase(cancelItem.pending, (state) => {
        state.submitting = true;
      })
      .addCase(cancelItem.fulfilled, (state, action) => {
        state.submitting = false;
        replaceItem(state, action.payload);
      })
      .addCase(cancelItem.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload?.message || 'Failed to cancel notification';
      })
      .addCase(fetchConfig.fulfilled, (state, action) => {
        state.config = action.payload;
      })
      .addCase(fetchConfig.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to load notification settings';
      })
      .addCase(updateConfig.pending, (state) => {
        state.submitting = true;
      })
      .addCase(updateConfig.fulfilled, (state, action) => {
        state.submitting = false;
        state.config = action.payload;
      })
      .addCase(updateConfig.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload?.message || 'Failed to update notification settings';
      })
      .addCase(sendTest.fulfilled, (state) => {
        state.error = null;
      })
      .addCase(sendTest.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to send test notification';
      });
  },
});

export const notificationReducer = notificationSlice.reducer;
export const { clearNotificationError, setConfigEnabled } = notificationSlice.actions;

export const notificationActions = {
  ...notificationSlice.actions,
  fetchItems,
  createItem,
  updateItem,
  deleteItem,
  sendNow,
  cancelItem,
  fetchConfig,
  updateConfig,
  sendTest,
};

export const selectNotifications = (state) => state.notifications.items;
export const selectNotificationsStatus = (state) => state.notifications.status;
export const selectNotificationsConfig = (state) => state.notifications.config;